/**
 * MetricsLtvPage — 数据中台 · LTV 队列 L2-E（ADM-MTR-S005，1:1 移植原型 metrics-ltv.jsx）
 *
 * 三块：① Cohort 累计 LTV 热力表（行 = cohort 日 × 可选渠道/Campaign 维度 · 列 = D0–D90 里程碑
 * 累计人均 LTV；cohort_size / payers live，收入未就绪 → 热力格灰显 + 悬浮 PENDING 说明）；
 * ② LTV 累积曲线分渠道对比（收入未就绪 → PendingBlock）。
 * 数据走 useMetricsLtvCohorts（GET /api/v2/metrics/ltv-cohorts?app_scope&group_by&from&to——
 * 本页契约是 from/to 日窗 + group_by，非 range 闭集，故不复用共享 MetricsToolbar 的 range 段控，
 * 时间控件用 DateRangeButton 按契约呈现）。
 * 禁假数据：readiness 缺省视为未就绪，禁把回填前 "0" 当真值渲染热力表；原型演示 mode 不移植；
 * 导出按钮保持禁用（契约暂无 LTV 导出端点，收入队列就绪前亦无可导出值）。
 */

import { useMemo, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import {
  LTV_MILESTONES,
  type LtvCohortGroupBy,
  type MetricsLtvCohortRowView,
} from '~/data/adapters/metrics-ltv-adapter';
import { useApps, useMetricsLtvCohorts } from '~/data/queries';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { ChartSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { LineChartX } from '~/ui/charts';
import {
  Cal,
  CardHead,
  OPEN_APP_SWITCHER_EVENT,
  PENDING_REASON,
  PendTag,
  PendingBlock,
  StateGate,
  type StateGateMode,
  fmtUsdMinor,
} from './metrics-shared';

// ---------- 日窗（契约 from/to，YYYY-MM-DD） ----------

/** Date → 本地 YYYY-MM-DD（DateRangeButton 面板按本地日历取值，避免 toISOString 跨时区偏一天） */
function fmtDateLocal(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 默认日窗 = 近 90 天（对齐服务端 resolveCohortRange 缺省：to=今天、from=to-90d） */
function defaultLtvWindow(): { from: string; to: string } {
  const now = new Date();
  return { from: fmtDateLocal(new Date(now.getTime() - 90 * 86_400_000)), to: fmtDateLocal(now) };
}

/** 里程碑是否已到期（cohort 日 + N 天 ≤ 今天；未到期格显示「·」） */
function milestoneMatured(cohortDate: string, days: number, dataAsOf?: string | null): boolean {
  const ms = Date.parse(`${cohortDate}T00:00:00Z`);
  return (
    Number.isFinite(ms) && ms + days * 86_400_000 <= (dataAsOf ? Date.parse(dataAsOf) : Date.now())
  );
}

const GROUP_BY_LABELS: Array<{ key: LtvCohortGroupBy; label: string }> = [
  { key: 'none', label: '不分组' },
  { key: 'media_source', label: '渠道' },
  { key: 'campaign', label: 'Campaign' },
];

const CURVE_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
];

export default function MetricsLtvPage() {
  const appId = useAppScope((s) => s.appId);
  const setAppId = useAppScope((s) => s.setAppId);
  const [groupBy, setGroupBy] = useState<LtvCohortGroupBy>('none');
  const [window_, setWindow] = useState(defaultLtvWindow);
  const [customWindow, setCustomWindow] = useState(false);

  const { data, isLoading, isError, serviceUpdating, hasData, refetch } = useMetricsLtvCohorts({
    appScope: appId,
    groupBy,
    from: customWindow ? window_.from : undefined,
    to: customWindow ? window_.to : undefined,
  });
  const { data: apps } = useApps();
  const currentAppName = appId === 'all' ? undefined : apps.find((a) => a.id === appId)?.name;
  const isAll = appId === 'all';

  const mode: StateGateMode = isLoading
    ? 'loading'
    : isError
      ? 'error'
      : hasData && !isError && data.rows.length === 0
        ? 'empty'
        : 'normal';

  // 本页日窗用户自选（无服务端 window 字段）；computed_at 是回填时刻而非数据覆盖上界，
  // 标「数据更新至」会虚报覆盖一天 → 如实标「最近回填」
  const freshness = data.computedAt ? `最近回填 ${data.computedAt.slice(0, 10)} · T+1` : undefined;

  // 热力色阶分母：全表最大人均里程碑值（仅就绪 + 已到期格参与；≤0 时不着色）
  const maxPerUser = useMemo(() => {
    if (!data.revenueReady) return 0;
    let max = 0;
    for (const r of data.rows) {
      if (!r.revenueMinor || (r.revenueCohortSize ?? 0) <= 0) continue;
      for (const m of LTV_MILESTONES) {
        if (!milestoneMatured(r.cohortDate, m.days, data.incomeDataAsOf)) continue;
        const v = r.revenueMinor[m.key] / (r.revenueCohortSize as number);
        if (v > max) max = v;
      }
    }
    return max;
  }, [data]);

  // 累积曲线系列：按当前分组聚合（人均 = Σ已到期 revenue ÷ Σ对应 cohort_size；无到期行 → null 断线）
  const curveSeries = useMemo(() => {
    if (!data.revenueReady) return [];
    // 分组键用 JSON 元组：维度值可含展示分隔符「 / 」（media_source='a / b' + campaign='c' 与
    // 'a' + 'b / c' 拼接后同串），字符串拼接会把不同 campaign 撞并成一条曲线；label 仅作展示
    const groups = new Map<string, { label: string; rows: MetricsLtvCohortRowView[] }>();
    for (const r of data.rows) {
      if (!r.revenueMinor) continue;
      const key =
        groupBy === 'none' ? '全部' : JSON.stringify([r.dimMediaSource ?? null, r.dimKey ?? null]);
      const label =
        groupBy === 'none'
          ? '全部'
          : groupBy === 'campaign'
            ? `${r.dimMediaSource ?? '—'} / ${r.dimKey ?? '—'}`
            : (r.dimKey ?? '—');
      const g = groups.get(key);
      if (g) g.rows.push(r);
      else groups.set(key, { label, rows: [r] });
    }
    return [...groups.values()].slice(0, CURVE_COLORS.length).map(({ label, rows }, i) => ({
      label,
      color: CURVE_COLORS[i] ?? 'var(--chart-1)',
      values: LTV_MILESTONES.map((m) => {
        let rev = 0;
        let size = 0;
        for (const r of rows) {
          if (!r.revenueMinor || !milestoneMatured(r.cohortDate, m.days, data.incomeDataAsOf))
            continue;
          rev += r.revenueMinor[m.key];
          size += r.revenueCohortSize ?? 0;
        }
        return size > 0 ? rev / size / 100 : null;
      }),
    }));
  }, [data, groupBy]);

  const dimCols: Array<{ label: string; of: (r: MetricsLtvCohortRowView) => string }> =
    groupBy === 'media_source'
      ? [{ label: '渠道', of: (r) => r.dimKey ?? '—' }]
      : groupBy === 'campaign'
        ? [
            { label: '渠道', of: (r) => r.dimMediaSource ?? '—' },
            { label: 'Campaign', of: (r) => r.dimKey ?? '—' },
          ]
        : [];

  return (
    <div className="page" data-screen-label="LTV 队列 L2-E">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            LTV 队列{' '}
            <span style={{ color: 'var(--fg-faint)', fontWeight: 400, fontSize: 15 }}>L2-E</span>
          </h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            安装日 cohort × D0–D90 累计净收入 · cohort_metrics_daily nightly 回填
            <Cal t="USD·运营口径·非结算" />
            <Cal t="T+1" />
            {!data.revenueReady && <PendTag k="rev" label="整页 PENDING·收入" />}
          </div>
        </div>
        <button
          type="button"
          className="btn"
          disabled
          title="收入队列就绪后可导出（契约暂无 LTV 导出端点）"
          style={{ opacity: 0.55, cursor: 'not-allowed' }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      {/* 本页顶栏：App 段控与共享 MetricsToolbar 1:1，时间控件按契约呈现 from/to 日窗（非 range 段控）+ group_by 维度段控 */}
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 2 }}>
        <div className="seg" title="数据范围：全部 App 聚合 / 单 App（复用全局 App 选择器）">
          <button type="button" className={isAll ? 'on' : ''} onClick={() => setAppId('all')}>
            全部 App
          </button>
          <button
            type="button"
            className={!isAll ? 'on' : ''}
            onClick={() => window.dispatchEvent(new Event(OPEN_APP_SWITCHER_EVENT))}
          >
            {isAll ? '单 App…' : currentAppName || '单 App'}
          </button>
        </div>
        <DateRangeButton
          value={[new Date(`${window_.from}T00:00:00`), new Date(`${window_.to}T00:00:00`)]}
          onChange={([f, t]) => {
            setCustomWindow(true);
            setWindow({ from: fmtDateLocal(f), to: fmtDateLocal(t) });
          }}
          defaultQuick="90d"
          buttonClass="btn btn-sm"
        />
        <div className="seg" title="分组维度：cohort 日 × 渠道（media_source）/ Campaign">
          {GROUP_BY_LABELS.map((g) => (
            <button
              key={g.key}
              type="button"
              className={groupBy === g.key ? 'on' : ''}
              onClick={() => setGroupBy(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>
        {freshness && (
          <span
            className="mono"
            style={{ fontSize: 10.5, color: 'var(--fg-faint)', padding: '0 4px' }}
            title="nightly Cron T+1 聚合 · KV 缓存 60s"
          >
            {freshness}
          </span>
        )}
        <div style={{ flex: 1 }} />
      </div>

      <StateGate
        mode={mode}
        skeleton={
          <>
            <TableSkeleton rows={9} cols={9} />
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
              <ChartSkeleton height={230} />
              <ChartSkeleton height={230} />
            </div>
          </>
        }
        emptyTitle="当前范围暂无 cohort 数据"
        emptyNote="cohort_metrics_daily 在该范围内无已回填队列 · nightly Cron T+1 生成"
        errorNote={serviceUpdating ? '服务更新中，请稍后重试' : 'LTV 加载失败，请重试'}
        onRetry={refetch}
      >
        <>
          {data.incomeDataAsOf && (
            <div className="muted" role="status">
              数据截至 {data.incomeDataAsOf} · 收入窗口 {data.incomeWindow?.start} —{' '}
              {data.incomeWindow?.end}
            </div>
          )}
          {/* 热力表：行骨架（cohort 规模 / 付费人数）live · 收入未就绪时里程碑格灰显 */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div
              className="hstack"
              style={{ justifyContent: 'space-between', padding: '12px 14px' }}
            >
              <div>
                <div className="hstack" style={{ gap: 6 }}>
                  <span style={{ fontWeight: 500, fontSize: 13.5 }}>Cohort 累计 LTV 热力表</span>
                  <Cal t="USD·运营口径" />
                  <Cal t="人均" />
                </div>
                <div style={{ color: 'var(--fg-muted)', fontSize: 11.5, marginTop: 2 }}>
                  行 = cohort 日（规模 · 付费人数已就绪 · 归因口径）· 列 = 里程碑累计 LTV（$/user）
                  {!data.revenueReady && ' · 全部单元格待回填'}
                </div>
              </div>
              <div
                className="hstack"
                style={{ gap: 4, alignItems: 'center', fontSize: 10, color: 'var(--fg-faint)' }}
                title={data.revenueReady ? '色阶：数值越大越深' : '就绪后启用的色阶：数值越大越深'}
              >
                色阶示意
                {[0.15, 0.35, 0.55, 0.8].map((p) => (
                  <span
                    key={p}
                    style={{
                      width: 14,
                      height: 10,
                      borderRadius: 2,
                      background: `color-mix(in oklab, var(--accent) ${p * 90}%, var(--bg-muted))`,
                      opacity: data.revenueReady ? 1 : 0.35,
                    }}
                  />
                ))}
                {!data.revenueReady && <PendTag k="rev" />}
              </div>
            </div>
            <div style={{ position: 'relative', overflowX: 'auto', padding: '0 14px 12px' }}>
              <table
                style={{
                  borderCollapse: 'separate',
                  borderSpacing: 2,
                  fontSize: 11,
                  width: '100%',
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        padding: '4px 8px',
                        color: 'var(--fg-muted)',
                        fontWeight: 500,
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      cohort 日
                    </th>
                    {dimCols.map((c) => (
                      <th
                        key={c.label}
                        style={{
                          padding: '4px 8px',
                          color: 'var(--fg-muted)',
                          fontWeight: 500,
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.label}
                      </th>
                    ))}
                    <th
                      className="mono"
                      style={{
                        padding: '4px 8px',
                        color: 'var(--fg-muted)',
                        fontWeight: 500,
                        textAlign: 'right',
                      }}
                    >
                      Cohort
                    </th>
                    <th
                      className="mono"
                      title="cohort 内 D7 累计付费人数（交易口径 · live）"
                      style={{
                        padding: '4px 8px',
                        color: 'var(--fg-muted)',
                        fontWeight: 500,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      付费D7
                    </th>
                    <th
                      className="mono"
                      title="cohort 内 D30 累计付费人数（交易口径 · live）"
                      style={{
                        padding: '4px 8px',
                        color: 'var(--fg-muted)',
                        fontWeight: 500,
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      付费D30
                    </th>
                    {LTV_MILESTONES.map((m) => (
                      <th
                        key={m.key}
                        className="mono"
                        style={{
                          padding: '4px 6px',
                          color: 'var(--fg-faint)',
                          fontWeight: 500,
                          textAlign: 'center',
                          minWidth: 56,
                        }}
                      >
                        {m.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={`${r.cohortDate}|${r.dimMediaSource ?? ''}|${r.dimKey ?? ''}`}>
                      <td
                        className="mono"
                        style={{
                          padding: '2px 8px',
                          color: 'var(--fg-muted)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.cohortDate}
                      </td>
                      {dimCols.map((c) => (
                        <td
                          key={c.label}
                          className="mono"
                          style={{
                            padding: '2px 8px',
                            color: 'var(--fg-muted)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.of(r)}
                        </td>
                      ))}
                      <td
                        className="mono"
                        style={{ padding: '2px 8px', color: 'var(--fg)', textAlign: 'right' }}
                      >
                        {r.cohortSize.toLocaleString()}
                      </td>
                      <td
                        className="mono"
                        style={{ padding: '2px 8px', color: 'var(--fg-muted)', textAlign: 'right' }}
                      >
                        {r.payersD7.toLocaleString()}
                      </td>
                      <td
                        className="mono"
                        style={{ padding: '2px 8px', color: 'var(--fg-muted)', textAlign: 'right' }}
                      >
                        {r.payersD30.toLocaleString()}
                      </td>
                      {LTV_MILESTONES.map((m) => {
                        if (!data.revenueReady || !r.revenueMinor) {
                          return (
                            <td
                              key={m.key}
                              title={`${r.cohortDate} · ${m.label} · ${PENDING_REASON.rev}`}
                              style={{
                                textAlign: 'center',
                                height: 26,
                                borderRadius: 3,
                                background: 'var(--bg-subtle)',
                                color: 'var(--fg-faint)',
                                fontFamily: 'var(--font-mono)',
                                fontSize: 10,
                              }}
                            >
                              —
                            </td>
                          );
                        }
                        if (!milestoneMatured(r.cohortDate, m.days, data.incomeDataAsOf)) {
                          return (
                            <td
                              key={m.key}
                              title={`${r.cohortDate} · ${m.label} · 未到期`}
                              style={{
                                textAlign: 'center',
                                height: 26,
                                borderRadius: 3,
                                color: 'var(--fg-faint)',
                                fontFamily: 'var(--font-mono)',
                                fontSize: 10,
                              }}
                            >
                              ·
                            </td>
                          );
                        }
                        const perUser =
                          (r.revenueCohortSize ?? 0) > 0
                            ? r.revenueMinor[m.key] / (r.revenueCohortSize as number)
                            : null;
                        const p = maxPerUser > 0 && perUser !== null ? perUser / maxPerUser : 0;
                        return (
                          <td
                            key={m.key}
                            title={`${r.cohortDate} · ${m.label} · 人均 ${fmtUsdMinor(perUser)}`}
                            style={{
                              textAlign: 'center',
                              height: 26,
                              borderRadius: 3,
                              background: `color-mix(in oklab, var(--accent) ${Math.round(p * 90)}%, var(--bg-muted))`,
                              // 高强度格背景深→白字保对比，低强度用常规前景色（p 为就绪态归一强度）
                              color: p > 0.5 ? 'white' : 'var(--fg)',
                              fontFamily: 'var(--font-mono)',
                              fontSize: 10,
                            }}
                          >
                            {fmtUsdMinor(perUser)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* 居中悬浮说明（不遮挡行骨架语义） */}
              {!data.revenueReady && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    className="hstack"
                    style={{
                      gap: 8,
                      padding: '8px 14px',
                      background: 'var(--bg-elev)',
                      border: '1px dashed var(--border-strong)',
                      borderRadius: 6,
                      boxShadow: 'var(--shadow-xs)',
                    }}
                  >
                    <Icons.IconLayers size={13} style={{ color: 'var(--fg-faint)' }} />
                    <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
                      PENDING_INSTRUMENTATION · 待收入接入 · 就绪后 nightly 回填逐列点亮
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div
              style={{
                padding: '8px 14px',
                borderTop: '1px solid var(--border)',
                fontSize: 10.5,
                color: 'var(--fg-faint)',
                background: 'var(--bg-subtle)',
              }}
            >
              cohort 规模来自 user_attributions（已就绪）· LTV_Dn = cohort 累计净收入 ÷
              cohort_size，待 purchase_amounts USD 分析列 · 回填后「·」表示未到期
            </div>
          </div>

          {/* 累积曲线：收入就绪 → 按当前分组出线；未就绪 → PENDING */}
          <div className="card" style={{ padding: 14, minWidth: 0 }}>
            <CardHead
              title="LTV 累积曲线 · 分渠道对比"
              sub="D0–D90 · $/user · 按渠道（media_source）对比 · 含自然量对照"
              tags={['USD·运营口径', 'T+1']}
              action={data.revenueReady ? undefined : <PendTag k="rev" />}
            />
            {data.revenueReady && curveSeries.length > 0 ? (
              <LineChartX
                series={curveSeries}
                xLabels={LTV_MILESTONES.map((m) => m.label)}
                height={228}
                yFormat={(v) => `$${Number(v).toFixed(2)}`}
              />
            ) : (
              <PendingBlock
                height={228}
                schematic
                icon="IconChart"
                title="累积曲线待收入队列回填"
                note="渠道维度（media_source）已就绪 · 曲线值待收入接入 · 就绪后按渠道自动出线"
              />
            )}
          </div>
        </>
      </StateGate>
    </div>
  );
}
